import AppIcon from "@/app/components/atomic/AppIcon"

export interface ChoiceCardOption<T extends string | number> {
    value: T
    title: string
    description?: string
    icon: string
    dataTest?: string
}

interface ChoiceCardGroupProps<T extends string | number> {
    options: ChoiceCardOption<T>[]
    // Unused in chevron mode: the card fires its action instead of holding a selected state.
    value?: T | null
    onChange: (value: T) => void
    ariaLabel: string
    columns?: 1 | 2
    layout?: "row" | "stacked-then-row"
    indicator?: "radio" | "chevron"
    className?: string
}

const COLUMNS_CLASS: Record<1 | 2, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
}

const Radio = ({ isSelected, className = "" }: { isSelected: boolean; className?: string }) => (
    <span
        aria-hidden="true"
        className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
            isSelected ? "border-rm-blue" : "border-rm-gray"
        } ${className}`}
    >
        {isSelected && <span className="size-2.5 rounded-full bg-rm-blue" />}
    </span>
)

export default function ChoiceCardGroup<T extends string | number>({
    options,
    value,
    onChange,
    ariaLabel,
    columns = 1,
    layout = "row",
    indicator = "radio",
    className = "",
}: ChoiceCardGroupProps<T>) {
    const stacked = layout === "stacked-then-row"
    const isChevron = indicator === "chevron"
    const indicatorPosition = stacked ? "ml-auto lg:order-last lg:ml-0" : "order-last"

    return (
        <div
            role={isChevron ? "group" : "radiogroup"}
            aria-label={ariaLabel}
            className={`grid gap-3 ${COLUMNS_CLASS[columns]} ${className}`}
        >
            {options.map((option) => {
                const isSelected = !isChevron && value === option.value

                return (
                    <button
                        key={String(option.value)}
                        type="button"
                        role={isChevron ? undefined : "radio"}
                        aria-checked={isChevron ? undefined : isSelected}
                        data-test={option.dataTest}
                        onClick={() => onChange(option.value)}
                        className={`flex h-full w-full cursor-pointer gap-3 rounded-2xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${
                            stacked
                                ? "flex-wrap items-start lg:flex-nowrap lg:items-center"
                                : "flex-nowrap items-center"
                        } ${isSelected ? "border-rm-blue bg-white" : "border-rm-gray-light bg-white hover:border-rm-gray"}`}
                    >
                        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-neutral">
                            <AppIcon iconClassName={option.icon} size="lg" className="text-pale" />
                        </span>

                        {isChevron ? (
                            <AppIcon
                                iconClassName="fa-chevron-right"
                                size="lg"
                                className={`shrink-0 text-rm-orange ${indicatorPosition}`}
                            />
                        ) : (
                            <Radio isSelected={isSelected} className={indicatorPosition} />
                        )}

                        <span
                            className={`flex flex-col gap-0.5 ${
                                stacked ? "order-last w-full lg:order-none lg:w-auto lg:flex-1" : "flex-1"
                            }`}
                        >
                            <span className="font-bold leading-snug text-pale">{option.title}</span>
                            {option.description && (
                                <span className="text-[13px] leading-snug text-rm-gray">{option.description}</span>
                            )}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}
